import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import logo from "../assets/logo.png";

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">{label}</div>
      <div className="text-sm text-slate-200 sm:text-right">{value || "—"}</div>
    </div>
  );
}

export default function ActivateServicePage() {
  const params = useMemo(() => (typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null), []);
  const context = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem("invoiceMateActivationContext");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [signedAtLabel, setSignedAtLabel] = useState("");
  const contractId = context?.contractId || String(params?.get("contractId") || "");

  useEffect(() => {
    if (!context?.signedAt) return;
    try {
      setSignedAtLabel(
        new Date(context.signedAt).toLocaleString([], {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } catch {
      setSignedAtLabel("");
    }
  }, [context]);

  return (
    <div className="min-h-screen bg-[#071120] text-white">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 pb-20 pt-32">
        <section className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[2rem] border border-cyan-300/12 bg-[linear-gradient(180deg,rgba(19,49,90,0.56),rgba(8,20,37,0.96))] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-cyan-400/10 ring-1 ring-cyan-300/20 shadow-[0_0_32px_rgba(34,211,238,0.24)]">
                <img src={logo} alt="MatrixOS logo" className="h-16 w-16 object-contain" />
              </div>
              <div>
                <div className="mb-2 inline-flex rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-200">Contract signed</div>
                <h1 className="text-4xl font-extrabold leading-tight text-white md:text-5xl">Next step: service activation</h1>
                <p className="mt-3 max-w-2xl text-lg leading-8 text-slate-300">
                  Your contract has been signed successfully. Trial usage is not billed. Once you choose which stores stay live after trial, Neo billing is calculated at the end of each month based on unique active staff per live store.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-4">
              <InfoRow label="Client" value={context?.fullName} />
              <InfoRow label="Company" value={context?.companyName} />
              <InfoRow label="Product" value={context?.productName} />
              <InfoRow label="Billing model" value={context?.billingModel} />
              <InfoRow label="Contract ID" value={contractId} />
              <InfoRow label="Signed" value={signedAtLabel} />
            </div>
          </div>

          <div className="rounded-[2rem] border border-cyan-300/12 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
            <div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Service activation</div>
            <h2 className="mt-3 text-3xl font-extrabold">What happens now</h2>
            <div className="mt-6 space-y-4 text-sm leading-7 text-slate-600">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">1. Start with the free trial</div>
                <p className="mt-1">You can begin with one free trial store for up to 30 days. Trial usage is not billed.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">2. Decide which stores stay live</div>
                <p className="mt-1">After trial, you choose which stores continue live on MatrixOS. Going live does not trigger an immediate charge.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">3. Billing happens after usage</div>
                <p className="mt-1">Neo billing is calculated at the end of each month based on unique active staff per live store during that month. SCS, SPS, and PLE remain billed at their agreed monthly per-store pricing.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">4. Monthly payment and grace period</div>
                <p className="mt-1">Payment is expected on the 1st of the following month. A 5-day grace period applies. After 5 days, selected functions may be restricted. After 7 days, live services may be suspended until payment is brought up to date.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-semibold text-slate-900">5. If you cancel after usage</div>
                <p className="mt-1">Cancellation stops future billing only going forward. Any live usage and charges already incurred before cancellation still remain payable to MatrixOS.</p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-sm leading-6 text-cyan-950">
              Checkout remains removed from this page. Billing and usage controls will be handled later through Super Admin.
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/pricing" className="rounded-full bg-cyan-400 px-6 py-3 font-semibold text-[#071120] transition hover:bg-cyan-300">View pricing</Link>
              <Link to="/" className="rounded-full border border-slate-200 px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-50">Return to homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
